import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Flex, Select, Text, LoadingOrbit, TagBadge } from '@chatsdet/ui';
import { API_URL } from '../../config.js';

type WorkflowSuiteJobPayload = {
  configId: string;
  triggerType: 'scheduled' | 'manual';
};

type BenchmarkConfig = {
  id: string;
  name: string;
  description: string | null;
  jobType: string;
  provider: string | null;
  model: string | null;
  isDefault: boolean;
  jobConfig: Record<string, unknown> | null;
};

type WorkflowSuiteJobConfigProps = {
  value: Partial<WorkflowSuiteJobPayload>;
  onChange: (payload: WorkflowSuiteJobPayload) => void;
  disabled?: boolean;
};

export function WorkflowSuiteJobConfig({ value, onChange, disabled = false }: WorkflowSuiteJobConfigProps) {
  const [configId, setConfigId] = React.useState<string>(value.configId || '');

  const configsQuery = useQuery({
    queryKey: ['benchmark-configs-workflow-suite'],
    queryFn: async (): Promise<{ configs: BenchmarkConfig[] }> => {
      const res = await fetch(`${API_URL}/superadmin/benchmark/configs?jobType=workflow_suite`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch benchmark configs');
      return res.json();
    },
  });

  // Set default config when configs load
  React.useEffect(() => {
    if (!configId && configsQuery.data?.configs.length) {
      const defaultConfig = configsQuery.data.configs.find((c) => c.isDefault);
      const firstConfig = configsQuery.data.configs[0];
      setConfigId(defaultConfig?.id || firstConfig!.id);
    }
  }, [configsQuery.data?.configs, configId]);

  // Update payload when config changes
  React.useEffect(() => {
    if (configId) {
      onChange({ configId, triggerType: 'scheduled' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId]);

  const configOptions = React.useMemo(() => {
    return (configsQuery.data?.configs || []).map((config) => ({
      id: config.id,
      label: `${config.name}${config.isDefault ? ' (default)' : ''}`,
    }));
  }, [configsQuery.data]);

  const selectedConfig = React.useMemo(() => {
    return configsQuery.data?.configs.find((c) => c.id === configId);
  }, [configsQuery.data, configId]);

  if (configsQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-4">
        <LoadingOrbit size="md" />
      </Flex>
    );
  }

  if (!configsQuery.data?.configs.length) {
    return (
      <Text className="text-muted-foreground">
        No workflow suite configs found. Create one in the Benchmark Configs page first.
      </Text>
    );
  }

  return (
    <Flex direction="column" gap="md">
      <Select
        label="Workflow Suite Configuration"
        placeholder="Select a workflow suite config"
        items={configOptions}
        selectedKey={configId || undefined}
        onSelectionChange={(key) => setConfigId(String(key))}
        isDisabled={disabled || configsQuery.isLoading}
      />

      {selectedConfig && (
        <div className="bg-muted/30 rounded-md p-3 space-y-2">
          {selectedConfig.description && (
            <Text size="sm" className="text-muted-foreground">
              {selectedConfig.description}
            </Text>
          )}
          <Flex gap="sm" wrap align="center">
            <TagBadge name="Workflow Suite" color="gray" hideDot />
            {selectedConfig.jobConfig && (
              <>
                {(selectedConfig.jobConfig as Record<string, unknown>).runsPerWorkflow && (
                  <Text size="xs" className="text-muted-foreground">
                    {String((selectedConfig.jobConfig as Record<string, unknown>).runsPerWorkflow)} runs/workflow
                  </Text>
                )}
                {(selectedConfig.jobConfig as Record<string, unknown>).runTimeoutMinutes && (
                  <Text size="xs" className="text-muted-foreground">
                    {String((selectedConfig.jobConfig as Record<string, unknown>).runTimeoutMinutes)}m timeout
                  </Text>
                )}
              </>
            )}
          </Flex>
        </div>
      )}
    </Flex>
  );
}
